import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  auth,
  firebaseEnabled,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  googleProvider,
  signOut as fbSignOut,
} from "@/lib/firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);    // null = checking, false = not auth, object = auth
  const [booting, setBooting] = useState(true);
  const [fbUser, setFbUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setBooting(false);
    }
  }, []);

  // Subscribe to Firebase token changes. Refetch /me whenever ID token renews or user signs in/out
  useEffect(() => {
    if (!firebaseEnabled || !auth) {
      fetchMe();
      return;
    }
    const unsub = onIdTokenChanged(auth, async (fbU) => {
      setFbUser(fbU);
      if (fbU) {
        try {
          const { data } = await api.get("/auth/me");
          setUser(data);
        } catch {
          setUser(false);
        } finally {
          setBooting(false);
        }
      } else {
        // No Firebase user. Fall back to cookie-based mock auth check
        await fetchMe();
      }
    });
    return () => unsub && unsub();
  }, [fetchMe]);

  // Mock / demo login (cookie JWT)
  const loginMock = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  // Firebase email/password
  const loginFirebase = async (email, password) => {
    if (!firebaseEnabled) throw new Error("Firebase auth is not configured");
    await signInWithEmailAndPassword(auth, email, password);
    // onIdTokenChanged will fetch /me and setUser
  };

  // Firebase Google
  const loginGoogle = async () => {
    if (!firebaseEnabled) throw new Error("Firebase auth is not configured");
    await signInWithPopup(auth, googleProvider);
  };

  const logout = async () => {
    try { if (firebaseEnabled && auth?.currentUser) await fbSignOut(auth); } catch {}
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setFbUser(null);
  };

  const hasPerm = (...perms) => {
    if (!user || user.role === "customer") return false;
    if (user.is_owner || user.role === "admin") return true;
    const eff = user.permissions || [];
    if (eff.includes("*")) return true;
    return perms.every((p) => eff.includes(p));
  };

  return (
    <AuthContext.Provider value={{
      user, booting, fbUser,
      loginMock, loginFirebase, loginGoogle, logout,
      refresh: fetchMe,
      hasPerm,
      firebaseEnabled,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
